import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';
import FeaturedArticleCard from '@/shared-components/forms/add-board-content/shared-components/FeaturedArticleCard';
import Heading from '@/shared-components/text/components/Heading';
import { assignLocalizedPublicPath } from '@/utils/localePublicUrl';

import './styles/FeaturedSlider.css';

/** Normalize article shape for BoardCard, plus recentlyPublished flag. */
function adaptArticle(item) {
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const created = item.createdAt ? new Date(item.createdAt) : null;
  const now = Date.now();
  return {
    ...item,
    recentlyPublished: created ? now - created.getTime() <= sevenDaysMs : false,
    post_container: {
      post_title: item.title,
      post_description: item.description,
      post_cover: { image_id: item.coverImageId },
    },
    post_status: item.status,
    post_visits: item.visits,
    comment_count: item.comment_count ?? 0,
    post_like_count: item.post_like_count ?? 0,
    is_liked: item.is_liked,
  };
}

export default function FeaturedSlider({
  articles = [],
  category = '',
}) {
  const isMobileView = useMediaQuery({ query: '(max-width: 768px)' });
  const trackRef = useRef(null);
  const viewportRef = useRef(null);

  // visualIndex: includes clones at 0 and last+1; real slides start at 1
  const [visualIndex, setVisualIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  // LOCKED: only ever set to a non-empty array
  const [lockedArticles, setLockedArticles] = useState(articles);
  useEffect(() => {
    if (articles.length > 0) setLockedArticles(articles);
  }, [articles]);

  const articleCount = lockedArticles.length;
  const prevCountRef = useRef(articleCount);

  const renderArticleCard = (article, suffix) => {
    const adapted = adaptArticle(article);
    const baseId = article? (article._id || article.article_id || article.slug) : 'featured-article';
    const key = suffix ? `${baseId}-${suffix}` : baseId;

    return (
      <div key={key} className="featured-slider__slider-item">
        <div className="featured-card-wrapper">
          {adapted.recentlyPublished && (
            <div className="recently-published-badge">
                  Recently published
            </div>
          )}
          <FeaturedArticleCard
            article={adapted}
            onClickRead={() => assignLocalizedPublicPath(`/article/${adapted._id}`)}
          />
        </div>
      </div>
    );
  };

  // autoplay progression driving visualIndex
  useEffect(() => {
    if (articleCount === 0 || isMobileView) return;
    if (prevCountRef.current !== articleCount) {
      setVisualIndex(1);
      prevCountRef.current = articleCount;
    }
    const interval = setInterval(() => {
      setVisualIndex(prev => {
        let next = prev + 1;
        if (next > articleCount + 1) next = 1;
        return next;
      });
    }, 20000);
    return () => clearInterval(interval);
  }, [articleCount, isMobileView]);

  // build extended slides array with clones at front and end (desktop only)
  const extendedArticles = !isMobileView && articleCount > 0
    ? [lockedArticles[articleCount - 1], ...lockedArticles, lockedArticles[0]]
    : [];

  // apply transform when visualIndex changes
  useEffect(() => {
    if (articleCount === 0 || isMobileView) return;
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = transitionEnabled ? 'transform 0.5s ease' : 'none';
    track.style.transform = `translateX(-${visualIndex * 100}%)`;
  }, [visualIndex, transitionEnabled, articleCount, isMobileView]);

  // re-enable transition after snapping
  useEffect(() => {
    if (!transitionEnabled) {
      requestAnimationFrame(() => setTransitionEnabled(true));
    }
  }, [transitionEnabled]);

  // Prevent rapid wheel transitions
  const wheelLockedRef = useRef(false);
  const handleWheel = e => {
    if (wheelLockedRef.current) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
    if (e.deltaX > 30) {
      wheelLockedRef.current = true;
      handleNext();
    } else if (e.deltaX < -30) {
      wheelLockedRef.current = true;
      handlePrev();
    }
  };

  const handleTransitionEnd = () => {
    if (articleCount === 0) return;
    wheelLockedRef.current = false;
    if (visualIndex === 0) {
      setTransitionEnabled(false);
      setVisualIndex(articleCount);
    } else if (visualIndex === articleCount + 1) {
      setTransitionEnabled(false);
      setVisualIndex(1);
    }
  };

  const handleNext = () => {
    if (articleCount === 0) return;
    setVisualIndex(prev => {
      let next = prev + 1;
      if (next > articleCount + 1) next = 1;
      return next;
    });
  };

  const handlePrev = () => {
    if (articleCount === 0) return;
    setVisualIndex(prev => {
      let next = prev - 1;
      if (next < 0) next = articleCount;
      return next;
    });
  };

  // Attach wheel event to viewport
  useEffect(() => {
    if (isMobileView) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  }, [articleCount, isMobileView]);

  return (
    <div className="featured-slider__container">
      <div className="featured-slider__inner">
        <div className="featured-slider__header">
          <Heading className="featured-slider__category">
            {category}
          </Heading>
        </div>

        {articleCount > 0 && (
          isMobileView ? (
            <div className="featured-slider__mobile-scroll">
              {lockedArticles.map(article => renderArticleCard(article))}
            </div>
          ) : (
            <>
              <div className="featured-slider__viewport" ref={viewportRef}>
                <div
                  className="featured-slider__track"
                  ref={trackRef}
                  onTransitionEnd={handleTransitionEnd}
                >
                  {extendedArticles.map((item, idx) => {
                    const suffix = idx === 0
                      ? 'clone-start'
                      : idx === extendedArticles.length - 1
                        ? 'clone-end'
                        : undefined;
                    return renderArticleCard(item, suffix);
                  })}
                </div>
              </div>
              <PrevButton className="featured-slider__control prev" onClick={handlePrev} />
              <NextButton className="featured-slider__control next" onClick={handleNext} />
            </>
          )
        )}
      </div>
    </div>
  );
}
